import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useMyProvider } from '../../hooks/useMyProvider';
import { useDashboardMetrics } from '../../hooks/useDashboardMetrics';
import { useAlerts } from '../../hooks/useAlerts';
import { useSentimentTrends } from '../../hooks/useSentimentTrends';
import MetricCard from '../../components/Dashboard/MetricCard';
import SentimentGauge from '../../components/Dashboard/SentimentGauge';
import SentimentLineChart from '../../components/Charts/SentimentLineChart';
import PlatformPieChart from '../../components/Charts/PlatformPieChart';
import TopicChip from '../../components/Dashboard/TopicChip';
import AlertBadge from '../../components/Dashboard/AlertBadge';
import NotAvailable from '../../components/Common/NotAvailable';
import { Severity } from '../../types/analytics';

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { provider, loading: providerLoading, error: providerError } = useMyProvider();
  const { metrics, loading: metricsLoading, refresh } = useDashboardMetrics(provider?.id || null);
  const {
    alerts,
    unacknowledgedCount,
    loading: alertsLoading,
  } = useAlerts(provider?.id || null, 7);
  const { data: trendsData, loading: trendsLoading } = useSentimentTrends(
    provider?.id || null,
    'daily',
    30
  );

  const handleRefresh = async () => {
    await refresh();
  };

  if (providerLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="font-['Open_Sans'] text-sm text-[#767472]">
          {providerError || 'No provider profile found. Create your provider profile to see analytics.'}
        </p>
      </div>
    );
  }

  const recentAlerts = alerts.filter((a) => !a.acknowledged).slice(0, 5);

  // `metrics` is null when the backend has no analysed reviews (404) or the request
  // failed; individual values are null when there is nothing to compute them from.
  // Missing values render as <NotAvailable />, never as 0.
  const notAvailable = <NotAvailable className="text-base" />;
  const healthScore = metrics?.sentiment_health_score ?? null;
  const avgQualityScore = metrics?.avg_quality_score ?? null;

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Ovo'] text-2xl md:text-3xl text-[#1f1f1f] mb-2">
            Analytics Overview
          </h1>
          {metrics && (
            <p className="font-['Open_Sans'] text-sm text-[#767472]">
              Last updated: {format(new Date(metrics.last_updated), 'MMM dd, yyyy HH:mm')}
            </p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={metricsLoading}
          className="px-4 py-2 bg-[#c6a664] text-white font-['Open_Sans'] text-sm font-semibold uppercase rounded hover:bg-[#b89854] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {metricsLoading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Sentiment Health"
          value={healthScore ?? notAvailable}
          subtitle={
            <>
              {healthScore !== null && <span className="block">out of 100</span>}
              {metrics && !metrics.trend_indicator && (
                <span className="block">
                  Trend: <NotAvailable />
                </span>
              )}
            </>
          }
          trend={metrics?.trend_indicator ?? undefined}
          loading={metricsLoading}
        />
        <MetricCard
          title="Total Reviews"
          value={metrics ? metrics.total_reviews : notAvailable}
          subtitle="all platforms"
          loading={metricsLoading}
          icon={
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          }
        />
        <MetricCard
          title="Avg Quality Score"
          value={avgQualityScore !== null ? avgQualityScore.toFixed(2) : notAvailable}
          subtitle="quality rating"
          loading={metricsLoading}
          icon={
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
        <MetricCard
          title="Recent Reviews"
          value={metrics ? metrics.recent_review_count : notAvailable}
          subtitle="last 7 days"
          loading={metricsLoading}
          icon={
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                clipRule="evenodd"
              />
            </svg>
          }
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-8">
          {/* Sentiment Health Gauge */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="font-['Ovo'] text-xl text-[#1f1f1f] mb-6">Sentiment Health Score</h2>
            {metricsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
              </div>
            ) : healthScore !== null ? (
              <div className="flex justify-center">
                <SentimentGauge score={healthScore} />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <NotAvailable className="text-sm" />
              </div>
            )}
          </div>

          {/* Sentiment Trend Chart */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Ovo'] text-xl text-[#1f1f1f]">30-Day Sentiment Trend</h2>
              <button
                onClick={() => navigate('/dashboard/analytics/sentiment')}
                className="font-['Open_Sans'] text-sm text-[#c6a664] hover:text-[#b89854] transition-colors"
              >
                View Details →
              </button>
            </div>
            {trendsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
              </div>
            ) : trendsData && trendsData.data_points.length > 0 ? (
              <SentimentLineChart data={trendsData.data_points} showMovingAverage />
            ) : (
              <div className="flex items-center justify-center h-64 text-[#767472]">
                <p className="font-['Open_Sans'] text-sm">No trend data available</p>
              </div>
            )}
          </div>

          {/* Platform Breakdown */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="font-['Ovo'] text-xl text-[#1f1f1f] mb-6">Platform Distribution</h2>
            {metricsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
              </div>
            ) : metrics && Object.keys(metrics.platform_breakdown).length > 0 ? (
              <PlatformPieChart data={metrics.platform_breakdown} />
            ) : (
              <div className="flex items-center justify-center h-64 text-[#767472]">
                <p className="font-['Open_Sans'] text-sm">No platform data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-8">
          {/* Top Topics */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Ovo'] text-lg text-[#1f1f1f]">Top Topics</h2>
              <button
                onClick={() => navigate('/dashboard/analytics/topics')}
                className="font-['Open_Sans'] text-xs text-[#c6a664] hover:text-[#b89854] transition-colors"
              >
                View All →
              </button>
            </div>

            <div className="space-y-4">
              {/* Positive Topics */}
              <div>
                <h3 className="font-['Open_Sans'] text-sm font-semibold text-green-600 mb-2">
                  Positive
                </h3>
                <div className="flex flex-wrap gap-2">
                  {metricsLoading ? (
                    <div className="h-6 bg-gray-200 rounded w-full animate-pulse"></div>
                  ) : metrics && metrics.top_positive_topics.length > 0 ? (
                    metrics.top_positive_topics
                      .slice(0, 5)
                      .map((topic, index) => (
                        <TopicChip key={index} topic={topic} sentiment={0.5} />
                      ))
                  ) : (
                    <p className="font-['Open_Sans'] text-xs text-[#767472]">No topics available</p>
                  )}
                </div>
              </div>

              {/* Negative Topics */}
              <div>
                <h3 className="font-['Open_Sans'] text-sm font-semibold text-red-600 mb-2">
                  Negative
                </h3>
                <div className="flex flex-wrap gap-2">
                  {metricsLoading ? (
                    <div className="h-6 bg-gray-200 rounded w-full animate-pulse"></div>
                  ) : metrics && metrics.top_negative_topics.length > 0 ? (
                    metrics.top_negative_topics
                      .slice(0, 5)
                      .map((topic, index) => (
                        <TopicChip key={index} topic={topic} sentiment={-0.5} />
                      ))
                  ) : (
                    <p className="font-['Open_Sans'] text-xs text-[#767472]">No topics available</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Alerts */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Ovo'] text-lg text-[#1f1f1f]">Recent Alerts</h2>
              <button
                onClick={() => navigate('/dashboard/analytics/alerts')}
                className="font-['Open_Sans'] text-xs text-[#c6a664] hover:text-[#b89854] transition-colors"
              >
                View All →
              </button>
            </div>

            {unacknowledgedCount > 0 && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
                <p className="font-['Open_Sans'] text-sm text-orange-800">
                  You have <strong>{unacknowledgedCount}</strong> unacknowledged alert
                  {unacknowledgedCount > 1 ? 's' : ''}
                </p>
              </div>
            )}

            <div className="space-y-3">
              {alertsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : recentAlerts.length > 0 ? (
                recentAlerts.map((alert) => (
                  <div
                    key={alert.alert_id}
                    className="p-3 border border-[#eaeaea] rounded hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate('/dashboard/analytics/alerts')}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <AlertBadge severity={alert.severity as Severity} withIcon />
                      <span className="font-['Open_Sans'] text-xs text-[#767472]">
                        {format(new Date(alert.created_at), 'MMM dd')}
                      </span>
                    </div>
                    <p className="font-['Open_Sans'] text-sm text-[#1f1f1f] line-clamp-2">
                      {alert.message}
                    </p>
                  </div>
                ))
              ) : (
                <p className="font-['Open_Sans'] text-sm text-[#767472] text-center py-4">
                  No recent alerts
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
